import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Eye } from 'lucide-react';
import { adminApi } from '../../services/api';

const CAPABILITIES = ['chat', 'embedding', 'document_processing', 'streaming', 'guardrail_validation', 'sentiment_analysis', 'conversation_summary', 'fallback_response'];

const emptyForm = () => ({
  provider: 'gemini',
  modelKey: '',
  displayName: '',
  apiModelName: '',
  capabilities: [] as string[],
  maxOutputTokens: '',
  temperature: '',
  topP: '',
  topK: '',
  inputCostPer1MTokens: '',
  outputCostPer1MTokens: '',
  isActive: true,
});

const AIModels: React.FC = () => {
  const [models, setModels] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [viewing, setViewing] = useState<any | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });

  const fetchModels = async () => {
    setLoading(true);
    try {
      const res = await adminApi.listAIModels({ page: pagination.page, limit: pagination.limit });
      const payload = res.data?.data as { models?: any[]; pagination?: { total?: number } } | undefined;
      setModels(payload && Array.isArray(payload.models) ? payload.models : []);
      setPagination(prev => ({ ...prev, total: payload?.pagination?.total ?? 0 }));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to load AI models');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchModels(); }, [pagination.page, pagination.limit]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setModalVisible(true);
  };

  const openEdit = (record: any) => {
    setEditing(record);
    setForm({
      provider: record.provider || 'gemini',
      modelKey: record.modelKey || '',
      displayName: record.displayName || '',
      apiModelName: record.apiModelName || '',
      capabilities: record.capabilities || [],
      maxOutputTokens: record.maxOutputTokens ?? '',
      temperature: record.temperature ?? '',
      topP: record.topP ?? '',
      topK: record.topK ?? '',
      inputCostPer1MTokens: record.inputCostPer1MTokens ?? '',
      outputCostPer1MTokens: record.outputCostPer1MTokens ?? '',
      isActive: record.isActive ?? true,
    });
    setModalVisible(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.displayName || !form.apiModelName) {
      toast.error('Display name and API model name are required');
      return;
    }
    try {
      const payload: any = {
        displayName: form.displayName,
        apiModelName: form.apiModelName,
        capabilities: form.capabilities,
        isActive: form.isActive,
        ...(form.maxOutputTokens !== '' && { maxOutputTokens: Number(form.maxOutputTokens) }),
        ...(form.temperature !== '' && { temperature: Number(form.temperature) }),
        ...(form.topP !== '' && { topP: Number(form.topP) }),
        ...(form.topK !== '' && { topK: Number(form.topK) }),
        ...(form.inputCostPer1MTokens !== '' && { inputCostPer1MTokens: Number(form.inputCostPer1MTokens) }),
        ...(form.outputCostPer1MTokens !== '' && { outputCostPer1MTokens: Number(form.outputCostPer1MTokens) }),
      };
      if (editing) {
        await adminApi.updateAIModel(editing._id, payload);
        toast.success('AI model updated');
      } else {
        payload.provider = form.provider;
        payload.modelKey = form.modelKey || form.apiModelName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        await adminApi.createAIModel(payload);
        toast.success('AI model created');
      }
      setModalVisible(false);
      fetchModels();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this model?')) return;
    try {
      await adminApi.deleteAIModel(id);
      toast.success('AI model deleted');
      fetchModels();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    }
  };

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  const toggleCapability = (cap: string) => {
    setForm(f => ({
      ...f,
      capabilities: f.capabilities.includes(cap)
        ? f.capabilities.filter(c => c !== cap)
        : [...f.capabilities, cap],
    }));
  };

  return (
    <div className="p-6 space-y-4">
      {/* Info banner */}
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <span className="font-medium">Note:</span>
        <span>Deprecated models (e.g. Gemini 2.0 Flash Lite, Gemini 1.5 Flash) are hidden from this list. For Gemini Flash Lite use API model name: <code className="font-mono bg-blue-100 px-1 rounded">gemini-2.5-flash-lite</code>.</span>
      </div>

      {/* Actions bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> Add AI model
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Provider','Model Key','Display Name','API Model','Max Tokens','Input $/1M','Output $/1M','Active','Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
              ) : models.length === 0 ? (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No models found</td></tr>
              ) : models.map(m => (
                <tr key={m._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-700">{m.provider}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{m.modelKey}</td>
                  <td className="px-4 py-3 text-gray-900">{m.displayName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">{m.apiModelName}</td>
                  <td className="px-4 py-3 text-gray-700">{m.maxOutputTokens ?? '–'}</td>
                  <td className="px-4 py-3 text-gray-700">{m.inputCostPer1MTokens != null ? m.inputCostPer1MTokens.toFixed(3) : '–'}</td>
                  <td className="px-4 py-3 text-gray-700">{m.outputCostPer1MTokens != null ? m.outputCostPer1MTokens.toFixed(3) : '–'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${m.isActive ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                      {m.isActive ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setViewing(m)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded" title="View details"><Eye className="w-3.5 h-3.5" /></button>
                      <button onClick={() => openEdit(m)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Pencil className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(m._id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {pagination.total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">Total: {pagination.total}</p>
            <div className="flex items-center gap-2">
              <button disabled={pagination.page <= 1 || loading} onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Prev</button>
              <span className="text-xs text-gray-600">Page {pagination.page} of {totalPages}</span>
              <button disabled={pagination.page >= totalPages || loading} onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">{editing ? 'Edit AI model' : 'Add AI model'}</h2>
              <button onClick={() => setModalVisible(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {!editing && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Provider <span className="text-red-500">*</span></label>
                    <select value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="gemini">gemini</option>
                      <option value="openai">openai</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Model key (unique) <span className="text-red-500">*</span></label>
                    <input value={form.modelKey} onChange={e => setForm(f => ({ ...f, modelKey: e.target.value }))} placeholder="e.g. gemini_flash" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display name <span className="text-red-500">*</span></label>
                <input value={form.displayName} onChange={e => setForm(f => ({ ...f, displayName: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">API model name <span className="text-red-500">*</span></label>
                <input value={form.apiModelName} onChange={e => setForm(f => ({ ...f, apiModelName: e.target.value }))} placeholder="e.g. gemini-2.5-flash-lite" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Capabilities</label>
                <div className="flex flex-wrap gap-2">
                  {CAPABILITIES.map(cap => (
                    <button key={cap} type="button" onClick={() => toggleCapability(cap)}
                      className={`px-2 py-1 text-xs rounded-md border ${form.capabilities.includes(cap) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}`}>
                      {cap}
                    </button>
                  ))}
                </div>
              </div>
              {[
                { key: 'maxOutputTokens', label: 'Max output tokens', step: '1', min: '1' },
                { key: 'temperature', label: 'Temperature', step: '0.1', min: '0', max: '2' },
                { key: 'topP', label: 'Top P', step: '0.1', min: '0', max: '1' },
                { key: 'topK', label: 'Top K', step: '1', min: '1' },
                { key: 'inputCostPer1MTokens', label: 'Input cost per 1M tokens (USD)', step: '0.01', min: '0', hint: 'Leave empty to use system default' },
                { key: 'outputCostPer1MTokens', label: 'Output cost per 1M tokens (USD)', step: '0.01', min: '0', hint: 'Leave empty to use system default' },
              ].map(({ key, label, step, min, max, hint }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                  <input type="number" step={step} min={min} max={max} value={(form as any)[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
                </div>
              ))}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">Active</label>
                <button type="button" onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.isActive ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setModalVisible(false)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View details modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-base font-semibold text-gray-900">AI Model Details</h2>
              <button onClick={() => setViewing(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div><span className="text-gray-500">Provider:</span> <span className="text-gray-900">{viewing.provider || '–'}</span></div>
              <div><span className="text-gray-500">Model key:</span> <span className="font-mono text-gray-900">{viewing.modelKey || '–'}</span></div>
              <div><span className="text-gray-500">Display name:</span> <span className="text-gray-900">{viewing.displayName || '–'}</span></div>
              <div><span className="text-gray-500">API model:</span> <span className="font-mono text-gray-900">{viewing.apiModelName || '–'}</span></div>
              <div><span className="text-gray-500">Capabilities:</span> <span className="text-gray-900">{Array.isArray(viewing.capabilities) && viewing.capabilities.length ? viewing.capabilities.join(', ') : '–'}</span></div>
              <div><span className="text-gray-500">Max output tokens:</span> <span className="text-gray-900">{viewing.maxOutputTokens ?? '–'}</span></div>
              <div><span className="text-gray-500">Temperature:</span> <span className="text-gray-900">{viewing.temperature ?? '–'}</span></div>
              <div><span className="text-gray-500">Top P:</span> <span className="text-gray-900">{viewing.topP ?? '–'}</span></div>
              <div><span className="text-gray-500">Top K:</span> <span className="text-gray-900">{viewing.topK ?? '–'}</span></div>
              <div><span className="text-gray-500">Input cost /1M:</span> <span className="text-gray-900">{viewing.inputCostPer1MTokens ?? '–'}</span></div>
              <div><span className="text-gray-500">Output cost /1M:</span> <span className="text-gray-900">{viewing.outputCostPer1MTokens ?? '–'}</span></div>
              <div><span className="text-gray-500">Status:</span> <span className="text-gray-900">{viewing.isActive ? 'Active' : 'Inactive'}</span></div>
            </div>
            <div className="flex justify-end gap-2 p-5 pt-0">
              <button onClick={() => setViewing(null)} className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50">Close</button>
              <button
                onClick={() => { const next = viewing; setViewing(null); openEdit(next); }}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Edit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIModels;
